import { z } from 'zod';
/**
 * Star ratings (Dax, 2026-10-01). Every account has one vote per item, one to five stars, and an item
 * shows the rounded mean of every vote it holds. A sound in the house library is one row every tenant
 * reads, so its votes come from every tenant and land on that one row. Unrated reads as three stars:
 * shown grey, and counted as three by the Composer, whose pool is everything at three or above unless
 * the request asks for higher.
 */
export const RATING_MAX_STARS = 5;
export const UNRATED_STARS = 3;
/** The three-dot menu lists the choices best first. */
export const RATING_MENU_STEPS = [5, 4, 3, 2, 1];
/** How many items one read may ask about: a page of any list, with room. */
export const RATINGS_READ_MAX = 1000;
export const RatingTargetKind = z.enum(['asset', 'snippet', 'phrase', 'audio_bed']);
export const RatingTarget = z.object({ kind: RatingTargetKind, id: z.string().uuid() });
export const Stars = z.number().int().min(1).max(RATING_MAX_STARS);
export const UNRATED = { stars: null, mean: null, votes: 0, mine: null };
export function ratingKey(t) {
    return `${t.kind}:${t.id}`;
}
/**
 * The rating once this account's vote becomes `stars` (null withdraws it), worked out from the mean and
 * the count alone, so a list can show the change before the api confirms it.
 */
export function withVote(r, stars) {
    const total = (r.mean ?? 0) * r.votes - (r.mine ?? 0) + (stars ?? 0);
    const votes = r.votes - (r.mine === null ? 0 : 1) + (stars === null ? 0 : 1);
    const mean = votes ? total / votes : null;
    return { stars: mean === null ? null : Math.round(mean), mean, votes, mine: stars };
}
/** The lowest rating the Composer may pick: what was asked for, else the unrated level. */
export function starsFloor(minStars) {
    return minStars ?? UNRATED_STARS;
}
/** Whether an item with this shown rating is in the Composer's pool; the SQL twin is ratingClears in @studio/db. */
export function ratingClears(stars, minStars) {
    return (stars ?? UNRATED_STARS) >= starsFloor(minStars);
}
const STAR_NUMBERS = { one: 1, two: 2, three: 3, four: 4, five: 5 };
const NUM = '(?:[1-5]|one|two|three|four|five)';
const ONLY = '(?:only|just|at\\s+least|minimum(?:\\s+of)?)';
/**
 * A star request in a prompt. A bare "five-star" is too often a description ("a five-star hotel
 * lobby"), so it counts only when qualified: a range ("4 or 5 stars"), a floor ("4+ stars", "at least
 * 4 stars", "4 stars and up"), "only", "rated", or a media word after it ("5 star clips").
 */
const STAR_ASK = new RegExp(`\\b(?<lead>${ONLY}\\s+)?(?<rated>rated\\s+(?:at\\s+least\\s+)?)?(?<a>${NUM})\\b(?:\\s*(?:or|-|–|to)\\s*(?<b>${NUM})\\b)?(?<plus>\\s*\\+)?`
    + '(?<stars>[\\s-]*stars?\\b)?(?<up>\\s+(?:and|or)\\s+(?:up|above|over|more|higher|better)\\b|\\s+plus\\b)?(?<only>\\s+only\\b)?', 'gi');
const TOP_RATED = new RegExp(`\\b(?:${ONLY}\\s+)?(?:the\\s+)?(?:top|highest|best)[\\s-]+rated\\b(?:\\s+only\\b)?`, 'i');
const MEDIA_AFTER = /^\s+(?:clips?|snippets?|videos?|footage|shots?|scenes?|sounds?|effects?|sfx|music|tracks?|songs?|audio|voiceovers?|material|content|items?|files?|assets?)\b/i;
/** "Top rated" asks for the top two steps. */
const TOP_RATED_STARS = 4;
function starNumber(word) {
    return STAR_NUMBERS[word.toLowerCase()] ?? Number(word);
}
function tidy(text) {
    return text.replace(/\s{2,}/g, ' ').replace(/\s+([,.;:!?])/g, '$1').replace(/^[\s,.;:·]+|[\s,;:·]+$/g, '').replace(/,\s*,/g, ',').trim();
}
/**
 * The lowest star rating a prompt asks for, and the prompt without those words so they never reach a
 * search as meaning. Deterministic on purpose: the Composer reads it before any Gemini call, on prompts
 * Gemini never sees (an Auto with words, a refresh), and identically every time.
 */
export function starsAsked(text) {
    for (const m of text.matchAll(STAR_ASK)) {
        const g = m.groups ?? {};
        const after = text.slice(m.index + m[0].length);
        const qualified = Boolean(g['rated']) || (Boolean(g['stars']) && Boolean(g['lead'] || g['only'] || g['up'] || g['plus'] || g['b'] || MEDIA_AFTER.test(after)));
        if (!qualified)
            continue;
        const a = starNumber(g['a']);
        const minStars = g['b'] ? Math.min(a, starNumber(g['b'])) : a;
        return { minStars, rest: tidy(text.slice(0, m.index) + after) };
    }
    const top = TOP_RATED.exec(text);
    if (top)
        return { minStars: TOP_RATED_STARS, rest: tidy(text.slice(0, top.index) + text.slice(top.index + top[0].length)) };
    return { minStars: null, rest: text };
}
//# sourceMappingURL=rating.js.map